import { assert } from 'assert-ts';
import { Suspense, useState } from 'react';
import { useSuspenseLighthouseQuery } from '#api/lighthouses.queries.ts';
import { useActiveCount } from '#hooks/useActiveCount.ts';
import { useIsManagedLighthouseCovered } from '#hooks/useIsManagedLighthouseCovered.ts';
import { DeleteHostDialog } from '#screens/hosts/delete/DeleteHostDialog.tsx';
import { isInfraHost } from '#util/host.ts';
import { DeleteLighthouseWarning } from './DeleteLighthouseWarning.tsx';

type Props = {
  lighthouseId: string;
  onDelete: () => void;
  onCancel: () => void;
};

export function DeleteLighthouseModal(props: Props) {
  // The boundary keeps the active-count fetch from bubbling to the page boundary, which would blank
  // the page on dialog open.
  return (
    <Suspense fallback={null}>
      <DeleteLighthouseModalContent {...props} />
    </Suspense>
  );
}

/**
 * Decides whether the lighthouse is the last active one. If it is, it presents a warning interstitial
 * before the deletion modal. Otherwise it shows the deletion modal directly.
 */
function DeleteLighthouseModalContent({ lighthouseId, onCancel, onDelete }: Props) {
  const [wasWarned, setWasWarned] = useState(false);
  const lighthouse = useSuspenseLighthouseQuery(lighthouseId).data.data;
  assert(isInfraHost(lighthouse), 'DeleteLighthouseModal requires a lighthouse');
  const { isLastActive: isLastLighthouse } = useActiveCount(lighthouse);
  const isMtlhCovered = useIsManagedLighthouseCovered(lighthouse.networkID);

  // When managed lighthouses cover the network, deleting the last self-hosted lighthouse isn't
  // disruptive — skip the warning interstitial and instead surface a reassurance banner in the
  // delete dialog.
  return isLastLighthouse && !wasWarned && !isMtlhCovered ?
      <DeleteLighthouseWarning onCancel={onCancel} onAccept={() => setWasWarned(true)} />
    : <DeleteHostDialog
        hostId={lighthouseId}
        onDelete={onDelete}
        onCancel={onCancel}
        managedLighthouseReassurance={isLastLighthouse && isMtlhCovered}
      />;
}
